import { ReportDocument } from "@/components/report-print/ReportDocument";
import { buildReportPages } from "@/components/report-print/reportLayout";
import type {
  ReportColumn,
  ReportDocumentModel,
  ReportPageBlock,
  ReportTableRow,
} from "@/components/report-print/reportTypes";
import type {
  PedidoExportDocument,
  PedidoExportStore,
  PedidoExportStoreItem,
} from "@/features/supplier/types";
import { compactVolumeBuckets, compactVolumeDisplay } from "./volumeDisplay";

export interface PedidoPrintPage {
  blocks: ReportPageBlock[];
  hasFooter: boolean;
}

interface PedidoPrintDocumentProps {
  document: PedidoExportDocument;
  printMode?: "source" | "preview";
}

const PEDIDO_COLUMNS: ReportColumn[] = [
  { key: "product", label: "Produto", width: "46%" },
  {
    key: "quantity",
    label: "Qtd.",
    width: "14%",
    align: "right",
    noWrap: true,
  },
  {
    key: "unitPrice",
    label: "Valor",
    width: "18%",
    align: "right",
    noWrap: true,
  },
  {
    key: "subtotal",
    label: "Subtotal",
    width: "22%",
    align: "right",
    noWrap: true,
  },
];

export function buildPedidoReportModel(
  document: PedidoExportDocument,
): ReportDocumentModel {
  return {
    header: {
      brand: "EloLink",
      logoSvg: document.logo_svg,
      documentLabel: "Pedido ao Fornecedor",
      title: document.supplier.name,
      subtitle: "",
      dateLabel: document.date,
      dateSubLabel: document.weekday,
    },
    sections: document.stores.map(buildStoreSection),
    footer: {
      brand: document.responsible.name || "EloLink",
      generatedLabel: `Gerado em ${document.generated_at}`,
      documentNumber: document.number,
    },
    continuationContext: `Pedido ao Fornecedor · ${document.supplier.name}`,
  };
}

export function buildPedidoPrintPages(
  document: PedidoExportDocument,
): PedidoPrintPage[] {
  return buildReportPages(buildPedidoReportModel(document)).map((page) => ({
    blocks: page.blocks,
    hasFooter: page.showFooter,
  }));
}

export function PedidoPrintDocument({
  document,
  printMode = "source",
}: PedidoPrintDocumentProps) {
  return (
    <ReportDocument
      document={buildPedidoReportModel(document)}
      printMode={printMode}
    />
  );
}

function buildStoreSection(store: PedidoExportStore) {
  return {
    id: `store-${store.index}`,
    title: `LOJA ${store.index} - ${store.name}`,
    subtitle: store.address,
    summary: {
      volume: storeVolumeSummary(store),
      money: store.total_formatted,
    },
    columns: PEDIDO_COLUMNS,
    rows: buildStoreRows(store.items),
  };
}

function buildStoreRows(items: PedidoExportStoreItem[]): ReportTableRow[] {
  if (items.length === 0) {
    return [emptyRow("store-empty", "Nenhum item para esta loja.")];
  }

  return items.map((item, index) => ({
    id: `item-${index}`,
    density:
      item.bonus && !isLongPedidoRowName(item.name)
        ? "compact"
        : isLongPedidoRowName(item.name)
          ? "wrapped"
          : "normal",
    tone: item.bonus ? "highlight" : "default",
    cells: [
      {
        columnKey: "product",
        value: item.name,
        secondary: productSecondary(item.package, item.weight),
      },
      {
        columnKey: "quantity",
        value: item.quantity,
        secondary: item.quantity_label,
        align: "right",
      },
      {
        columnKey: "unitPrice",
        value: item.unit_price,
        secondary: item.unit_price_label,
        align: "right",
      },
      {
        columnKey: "subtotal",
        value: item.subtotal,
        align: "right",
        emphasis: true,
      },
    ],
  }));
}

function emptyRow(id: string, message: string): ReportTableRow {
  return {
    id,
    tone: "muted",
    cells: [
      { columnKey: "product", value: message },
      { columnKey: "quantity", value: "", align: "right" },
      { columnKey: "unitPrice", value: "", align: "right" },
      { columnKey: "subtotal", value: "", align: "right" },
    ],
  };
}

function productSecondary(
  packageLabel: string | undefined,
  weight: string | undefined,
) {
  return [packageLabel, weight].filter(Boolean).join(" · ");
}

function bucketValue(value: string | undefined, fallback = "0") {
  return value && value.trim() !== "" ? value : fallback;
}

function storeVolumeSummary(store: PedidoExportStore) {
  return (
    compactVolumeDisplay(store.volume_summary) ??
    compactVolumeBuckets([
      [bucketValue(store.total_caixas, store.total_boxes), "cx"],
      [bucketValue(store.total_bandejas), "bdj"],
      [bucketValue(store.total_sacos), "sc"],
      [bucketValue(store.total_kg), "kg"],
      [bucketValue(store.total_unidades), "un"],
    ])
  );
}

function isLongPedidoRowName(value: string) {
  return value.length > 42;
}
